import { useState } from 'react';
import * as yup from 'yup';

import { Formik } from 'formik';
import { useTranslation } from '@packlink/translation-provider';
import { Address, IPostalCode } from '@packlink/packlink-sdk';
import { PostalCodeOption, CountryOption } from '@packlink/address-selectors';
import { PlaceOrigin } from '@types';
import { addressEditSchema } from '@validation';
import { AddressFormFields } from './AddressFormFields';

interface IAddressFormProps {
    /** Address model */
    initialValues: Address;
    /** Type of origin for this address. Either 'from' or 'to' */
    origin?: PlaceOrigin;
    /** Form title */
    title: string;
    /** On cancel success callback to be executed by the caller */
    onCancel(): void;
    /** On edition success callback to be executed by the caller */
    onSave(address: Address): void;
}

export type AddressFormValues = Pick<
    Address,
    'firstName' | 'lastName' | 'phone' | 'email' | 'street1' | 'street2' | 'company'
> & {
    country: CountryOption | null;
    postalCode: PostalCodeOption | null;
};

export const AddressForm = ({ initialValues, origin, title, onCancel, onSave }: IAddressFormProps): JSX.Element => {
    const { t } = useTranslation();
    /**
     * State handlers for alpha2Code and PostalCode.
     */
    const [selectedAlpha2Code, setSelectedAlpha2Code] = useState<string>(initialValues?.alpha2Code || '');
    const [selectedPostalCode, setSelectedPostalCode] = useState<IPostalCode | null>();
    /**
     * Parsers address model to form.
     */
    const parseAddressToAddressFormFields = (address: Address): AddressFormValues => {
        const { alpha2Code, zipCode, city, state, ...rest } = address;
        return {
            country: alpha2Code ? { label: state || '', value: alpha2Code || '' } : null,
            postalCode: zipCode ? { label: `${zipCode}${city ? ` - ${city}` : ''}`, value: zipCode } : null,
            ...rest,
        };
    };
    /**
     * Parsers form to address model.
     */
    const parseAddressFormFieldsToAddress = (addressFormFields: AddressFormValues): Address => {
        const { country, ...rest } = addressFormFields;
        return Address.deserialize({
            alpha2Code: country?.value,
            state: country?.label,
            zipCode: selectedPostalCode?.zipCode,
            city: selectedPostalCode?.city,
            ...rest,
        });
    };
    /**
     * Handle form submit.
     */
    const onSubmit = (data: AddressFormValues) => {
        onSave(parseAddressFormFieldsToAddress(data));
    };
    /**
     * Address form schema.
     */
    const schema = yup.object().shape(addressEditSchema(t)).required();

    return (
        <Formik
            initialValues={parseAddressToAddressFormFields(initialValues)}
            onSubmit={onSubmit}
            validationSchema={schema}
            validateOnBlur
            initialTouched={{ email: true, postalCode: true, country: true, phone: true }}
        >
            <AddressFormFields
                selectedAlpha2Code={selectedAlpha2Code}
                onCancel={onCancel}
                setSelectedPostalCode={setSelectedPostalCode}
                setSelectedAlpha2Code={setSelectedAlpha2Code}
                origin={origin}
                title={title}
            />
        </Formik>
    );
};
